// `<PhotoSourceButtons>` — on-demand photo collection actions (Plan 11-05).
//
// Renders two buttons near the HeroGallery on the property detail page:
//
//   1. "Get Zillow Photos"       — fires POST /photos/collect?source=zillow directly
//   2. "Deep Dive (all sources)" — opens a Radix Dialog confirm showing:
//        - A static credit estimate ("~13–18 credits") — NO credit spend to show this
//        - The REAL remaining Firecrawl balance, fetched from GET /api/credits via
//          useCreditBalance(deepOpen) — only fetches when the dialog is open (D-3/D-9)
//        - "balance unavailable" when balance is null (parse failure graceful fallback)
//        Fires POST /photos/collect?source=deep only on explicit Confirm.
//
// Security (T-11-CREDIT):
//   Deep Dive never fires on a single click. The confirm dialog is the gate.
//   Buttons disable while a job is running (T-11-API2: prevents double-spend).
//   The server-side fc_credit_check 20 floor (Plan 11-04) is the hard backstop.
//   The balance fetch itself is credit-free (parses firecrawl --status, no scrape).
//
// Design:
//   Composes Phase 05 Glass Coast primitives only. No new tokens, no new deps.
//   Radix Dialog imported from @radix-ui/react-dialog (already installed).
//   GlassPanel wraps dialog content (ocean/tide palette, existing tokens).
//
// Credit estimate note:
//   "~13–18 credits" is a static heuristic (DEEP_DIVE_CREDIT_ESTIMATE = 16 in server,
//   Plan 11-04). Shown in the dialog purely from this string constant — no API call.
//   The live remaining balance (fetched via useCreditBalance) is the *other* number
//   the user needs to make an informed decision before clicking Confirm (D-3/D-9).

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { usePhotoCollect, usePhotoJobStatus, useCreditBalance } from '@/hooks/usePhotoCollect';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

// Static heuristic — matches server DEEP_DIVE_CREDIT_ESTIMATE = 16.
// Displayed in the dialog with NO credit spend (D-3/D-9).
const DEEP_DIVE_ESTIMATE_LABEL = '~13–18 credits';

export interface PhotoSourceButtonsProps {
  propertyId: number;
  /**
   * Called when a collection error occurs. The caller (PropertyDetail) wires
   * this to the existing setToast so failures reuse the established toast pipeline.
   */
  onError?: (msg: string) => void;
}

/**
 * `<PhotoSourceButtons>` — photo collection action row.
 *
 * Props: `{ propertyId: number; onError?: (msg: string) => void }`.
 *
 * Renders inline below HeroGallery in PropertyDetail's SuccessLayout.
 * Disables both buttons while a photo job is running.
 * Shows a spinner + "Collecting photos…" when isRunning.
 */
export function PhotoSourceButtons({
  propertyId,
  onError,
}: PhotoSourceButtonsProps) {
  const [deepOpen, setDeepOpen] = useState(false);

  const mutation = usePhotoCollect(propertyId);
  const { data: jobStatus } = usePhotoJobStatus(propertyId);

  // useCreditBalance is enabled only when the Deep Dive dialog is open (D-3/D-9).
  // This gates the balance fetch to exactly when the user needs it — before Confirm.
  const { data: creditData, isLoading: creditsLoading } = useCreditBalance(deepOpen);

  // isRunning covers: an active photo_jobs row OR a pending mutation
  const isRunning =
    jobStatus?.status === 'running' || mutation.isPending;

  const photosAdded = jobStatus?.photos_added;

  // ---- Error handler -------------------------------------------------
  const handleError = (error: Error) => {
    let msg = 'Photo collection failed. Try again.';
    if (error instanceof ApiError && error.status === 409) {
      msg = 'Photo collection already in progress.';
    }
    onError?.(msg);
  };

  // ---- Zillow quick action -------------------------------------------
  const handleZillow = () => {
    mutation.mutate(
      { source: 'zillow' },
      { onError: handleError },
    );
  };

  // ---- Deep Dive confirm action -------------------------------------
  const handleDeepConfirm = () => {
    setDeepOpen(false);
    mutation.mutate(
      { source: 'deep' },
      { onError: handleError },
    );
  };

  // ---- Balance display -----------------------------------------------
  // Render "658 remaining", "balance unavailable", or "Loading…" depending
  // on fetch state. This + the static estimate satisfy D-3/D-9.
  const balanceLabel = creditsLoading
    ? 'Loading…'
    : creditData?.balance != null
      ? `${creditData.balance} remaining`
      : 'balance unavailable';

  // ---- Inline progress label -----------------------------------------
  const progressLabel = isRunning
    ? photosAdded != null
      ? `Collecting photos… · added ${photosAdded}`
      : 'Collecting photos…'
    : null;

  // ---- Shared button styles ------------------------------------------
  const baseBtn = cn(
    'inline-flex items-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold',
    'transition-colors duration-150',
    'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  );

  return (
    <div className="flex flex-wrap items-center gap-3 mt-3">
      {/* ---- Get Zillow Photos ---------------------------------------- */}
      <button
        type="button"
        disabled={isRunning}
        onClick={handleZillow}
        aria-label="Get Zillow Photos"
        className={cn(
          baseBtn,
          'bg-tide-400/20 text-tide-400 border border-tide-400/30',
          'hover:bg-tide-400/30',
        )}
      >
        {isRunning && mutation.variables?.source === 'zillow' && (
          <Loader2 aria-hidden className="w-4 h-4 animate-spin" />
        )}
        Get Zillow Photos
      </button>

      {/* ---- Deep Dive (confirm dialog) -------------------------------- */}
      <Dialog.Root open={deepOpen} onOpenChange={setDeepOpen}>
        <Dialog.Trigger asChild>
          <button
            type="button"
            disabled={isRunning}
            aria-label="Deep Dive (all sources)"
            className={cn(
              baseBtn,
              'bg-ocean-800/60 text-foam-50 border border-foam-50/15',
              'hover:bg-ocean-800/80',
            )}
          >
            {isRunning && mutation.variables?.source === 'deep' && (
              <Loader2 aria-hidden className="w-4 h-4 animate-spin" />
            )}
            Deep Dive (all sources)
          </button>
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-ocean-950/60 backdrop-blur-sm" />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[340px] max-w-[90vw]',
            )}
            aria-describedby="deep-dive-desc"
          >
            <GlassPanel
              variant="strong"
              className="p-6 rounded-2xl shadow-2xl border border-foam-50/10"
            >
              <Dialog.Title className="text-base font-semibold text-foam-50 mb-1">
                Deep Dive — All Sources
              </Dialog.Title>
              <Dialog.Description id="deep-dive-desc" className="text-sm text-foam-50/70 mb-4">
                Collects photos from Zillow, Realtor.com, Redfin, and web image search,
                then de-duplicates. This will spend Firecrawl credits.
              </Dialog.Description>

              {/* Cost visibility (D-3/D-9): estimate + real remaining balance */}
              <div className="mb-5 px-3 py-2 rounded-lg bg-ocean-900/60 border border-foam-50/10 text-sm text-foam-50">
                <span className="font-semibold">{DEEP_DIVE_ESTIMATE_LABEL}</span>
                <span className="mx-2 text-foam-50/40">·</span>
                <span className={creditData?.balance == null && !creditsLoading ? 'text-foam-50/50' : ''}>
                  {balanceLabel}
                </span>
              </div>

              <div className="flex gap-3 justify-end">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className={cn(
                      baseBtn,
                      'bg-transparent text-foam-50/70 border border-foam-50/15',
                      'hover:text-foam-50 hover:bg-foam-50/5',
                    )}
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={handleDeepConfirm}
                  className={cn(
                    baseBtn,
                    'bg-bronze-500 text-ocean-950',
                    'hover:bg-bronze-500/90',
                  )}
                >
                  Confirm
                </button>
              </div>
            </GlassPanel>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* ---- Inline progress indicator -------------------------------- */}
      {progressLabel && (
        <span
          className="inline-flex items-center gap-1.5 text-sm text-foam-50/70"
          aria-live="polite"
        >
          <Loader2 aria-hidden className="w-3.5 h-3.5 animate-spin" />
          {progressLabel}
        </span>
      )}
    </div>
  );
}

export default PhotoSourceButtons;
